import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MultiSelect.css';

/**
 * Multi-select dropdown with:
 *  - type-to-filter input
 *  - clicked values displayed as removable pills
 *  - outside-click to close
 *  - Clear button when any value selected
 *  - Dropdown rendered via portal to escape overflow clipping
 */
export default function MultiSelect({
  label,
  options,
  values,
  onChange,
  placeholder = 'All',
  minWidth = 180,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const portal = document.getElementById('ms-portal-drop');
        if (portal && portal.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (opt) => {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt));
    else onChange([...values, opt]);
    inputRef.current?.focus();
  };

  const remove = (opt) => onChange(values.filter((v) => v !== opt));
  const clear = () => { onChange([]); setQuery(''); };

  const handleInputFocus = () => setOpen(true);

  const dropdownEl = open && dropPos && createPortal(
    <div
      id="ms-portal-drop"
      className="ms-drop ms-drop-portal"
      style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width }}
    >
      {filteredOptions.length === 0 && (
        <div className="ms-empty">No matches</div>
      )}
      {filteredOptions.map((opt) => {
        const checked = values.includes(opt);
        return (
          <div
            key={opt}
            className={`ms-opt${checked ? ' sel' : ''}`}
            onClick={() => toggle(opt)}
          >
            <span className={`ms-check${checked ? ' on' : ''}`}>
              {checked ? '✓' : ''}
            </span>
            <span className="ms-opt-lbl">{opt}</span>
          </div>
        );
      })}
    </div>,
    document.body
  );

  return (
    <div className="ms-wrap" ref={wrapRef} style={{ minWidth }}>
      {label && <div className="ms-label">{label}</div>}
      <div
        className={`ms-control${open ? ' open' : ''}`}
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {values.map((v) => (
          <span key={v} className="ms-pill">
            {v}
            <button
              type="button"
              className="ms-pill-x"
              onClick={(e) => { e.stopPropagation(); remove(v); }}
              aria-label={`Remove ${v}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="ms-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={handleInputFocus}
          placeholder={values.length === 0 ? placeholder : ''}
        />
        {values.length > 0 && (
          <button
            type="button"
            className="ms-clear"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            title="Clear"
          >
            Clear
          </button>
        )}
      </div>
      {dropdownEl}
    </div>
  );
}
